import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Li, List, Box, Text } from '@nerdwallet/currency';
import MarketplaceReviewLink from '@nerdwallet/react-marketplace-review-link';
import StructuredContent from '@nerdwallet/structured-content';
import nds from '@nerdwallet/base-styles/classes';

import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import {
  getConsBullets as getStandardConsBullets,
  getDisplayName as getName,
  getProductType,
  getProsBullets as getStandardProsBullets,
} from '@nerdwallet/marketplace/selectors/common';
import {
  getConsBullets as getBankingConsBullets,
  getProsBullets as getBankingProsBullets,
} from '@nerdwallet/marketplace/selectors/banking';

import { getHardCodedCons, getHardCodedPros } from '../../../lib/banking/utils';

// For banking, we use hard-coded pros and cons at a custom path :(
// Until we get them moved into marketplace, we need a special mapping for banking
const getConsBullets = (marketplaceEntity) => {
  const productType = getProductType(marketplaceEntity);
  if (productType === PRODUCT_TYPES.BANKING) {
    return getHardCodedCons(marketplaceEntity);
  }

  return getStandardConsBullets(marketplaceEntity);
};
const getProsBullets = (marketplaceEntity) => {
  const productType = getProductType(marketplaceEntity);
  if (productType === PRODUCT_TYPES.BANKING) {
    return getHardCodedPros(marketplaceEntity);
  }

  return getStandardProsBullets(marketplaceEntity);
};

const bulletedList = (title, bullets, shouldRenderStructuredContent) => {
  if (_.isEmpty(bullets)) {
    return null;
  }

  return (
    <Fragment>
      <Text size="small" bold>
        {title}
      </Text>
      <List className={nds('margin-bottom-3')}>
        {bullets.map((bullet) => (
          <Li className={nds('type-1')} key={bullet}>
            {shouldRenderStructuredContent ? (
              <StructuredContent>{bullet}</StructuredContent>
            ) : (
              bullet
            )}
          </Li>
        ))}
      </List>
    </Fragment>
  );
};

const ProsAndCons = ({ marketplaceEntity }) => {
  const defaultBankingCons = getBankingConsBullets(marketplaceEntity);
  const defaultBankingPros = getBankingProsBullets(marketplaceEntity);

  /**
   * Banking additional data pros and cons are structured content.
   * If they exist, they must be rendered differently.
   */
  const shouldRenderStructuredPros = !_.isEmpty(defaultBankingPros);
  const shouldRenderStructuredCons = !_.isEmpty(defaultBankingCons);

  const cons = shouldRenderStructuredCons
    ? defaultBankingCons
    : getConsBullets(marketplaceEntity);
  const pros = shouldRenderStructuredPros
    ? defaultBankingPros
    : getProsBullets(marketplaceEntity);

  if (_.isEmpty(pros) && _.isEmpty(cons)) {
    return (
      <Box className="upt__custom-copy">
        <Text size="small">
          Learn more about the{' '}
          <MarketplaceReviewLink marketplaceEntity={marketplaceEntity}>
            {getName(marketplaceEntity)}
          </MarketplaceReviewLink>
          .
        </Text>
      </Box>
    );
  }

  return (
    <div style={{ minWidth: '218px' }} data-integration="pros-and-cons">
      {bulletedList('Pros', pros, shouldRenderStructuredPros)}
      {bulletedList('Cons', cons, shouldRenderStructuredCons)}
    </div>
  );
};

ProsAndCons.propTypes = {
  cons: PropTypes.arrayOf(PropTypes.string),
  marketplaceEntity: PropTypes.object,
  pros: PropTypes.arrayOf(PropTypes.string),
};

export default ProsAndCons;
